import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Receipt, Buildings, Clock } from "@phosphor-icons/react";
import TeamSeats from "../components/TeamSeats";

const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

export default function Billing() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    api.get("/billing/payment-info")
      .then((r) => setInfo(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const statusLabel = () => {
    if (!info) return "";
    if (info.plan === "free_trial") return info.locked ? "Trial ended" : "Free trial";
    if (info.locked) return "Access paused";
    return "Active";
  };
  const statusTone = () =>
    !info ? "" : (info.locked ? "bg-red-100 text-red-800" : info.plan === "free_trial" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800");

  return (
    <div className="max-w-4xl mx-auto space-y-8 py-2" data-testid="billing-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Plan & Billing</h1>
        <p className="text-sm text-slate-600 mt-1">ClaroShield is billed by invoice — no online checkout.</p>
      </div>

      <Card className="p-6 rounded-none border-slate-200" data-testid="billing-plan-card">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-none bg-primary/10 text-primary">
            <Buildings size={22} weight="fill" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-slate-400">Current plan</div>
            <div className="text-lg font-semibold text-slate-900" data-testid="billing-current-plan">
              {info?.plan_label || "—"}
            </div>
          </div>
          <Badge className={`rounded-none ${statusTone()}`} data-testid="billing-status">{statusLabel()}</Badge>
        </div>
        {(info?.trial_ends_at || info?.access_expires_at) && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-600 border-t border-slate-100 pt-3">
            <Clock size={15} />
            {info.plan === "free_trial"
              ? <span>Trial end date: <b>{fmtDate(info.trial_ends_at) || "—"}</b></span>
              : <span>Term ends: <b>{fmtDate(info.access_expires_at) || "No expiry"}</b></span>}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-none border-slate-200" data-testid="billing-payment-instructions">
        <div className="flex items-center gap-2 mb-3">
          <Receipt size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-slate-900">How to pay & upgrade</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          To upgrade your tier or renew, contact your ClaroShield representative — we'll issue an invoice
          referencing your PO number. Your access is enabled as soon as payment is confirmed.
        </p>
        <pre className="whitespace-pre-wrap text-sm text-slate-800 bg-slate-50 border border-slate-200 p-4 leading-relaxed font-sans">
{info?.payment_instructions || "Loading payment instructions…"}
        </pre>
      </Card>

      <TeamSeats />
    </div>
  );
}
